import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Clock, EnvelopeSimple, User, Phone, UserPlus, Bug, CheckCircle, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { getApiBaseUrl } from '../../utils/api';
import { toast } from 'sonner';
import { formatTime } from './utils';

/**
 * WaitlistDialog - Admin dialog for managing session waitlist
 * Allows promoting waitlisted players or notifying them of available spots
 */
export function WaitlistDialog({
  open,
  onOpenChange,
  session
}) {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingTo, setSendingTo] = useState(null);
  const [promotingTo, setPromotingTo] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [fixingPlayer, setFixingPlayer] = useState(null);

  useEffect(() => {
    if (open && session) {
      loadWaitlist();
    }
  }, [open, session]);

  const loadWaitlist = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/waitlist`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Map the response format to our expected format
        const formattedWaitlist = (data.waitlist || []).map((item, index) => ({
          registration_id: item.registration_id,
          player_id: item.player?.player_id,
          player_name: item.player?.name || 'Unknown',
          player_age: item.player?.age,
          parent_name: item.parent?.name || 'Unknown',
          parent_email: item.parent?.email,
          parent_phone: item.parent?.phone,
          joined_at: item.registered_at,
          position: item.position || index + 1,
          status: item.status || 'waitlisted'
        }));
        setWaitlist(formattedWaitlist);
        
        // Set session info from the response
        const sessionData = data.session || {};
        setSessionInfo({
          capacity: sessionData.capacity || 25,
          registered_count: sessionData.registered_count || 0,
          spots_available: Math.max(0, (sessionData.capacity || 25) - (sessionData.registered_count || 0)),
          waitlist_count: data.total || formattedWaitlist.length
        });
      } else {
        toast.error('Failed to load waitlist');
      }
    } catch (error) {
      console.error('Load waitlist error:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const loadDebugData = async () => {
    setLoadingDebug(true);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/debug-registrations`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
      } else {
        toast.error('Failed to load debug data');
      }
    } catch (error) {
      console.error('Debug load error:', error);
      toast.error('Failed to load debug data');
    } finally {
      setLoadingDebug(false);
    }
  };

  const handleForceAdd = async (playerId, playerName) => {
    setFixingPlayer(playerId);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/force-add/${playerId}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `${playerName} added to roster!`);
        loadDebugData();
        loadWaitlist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add player');
      }
    } catch (error) {
      console.error('Force add error:', error);
      toast.error('Failed to add player');
    } finally {
      setFixingPlayer(null);
    }
  };

  const handleNotify = async (registration) => {
    setSendingTo(registration.registration_id);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/waitlist/${registration.registration_id}/notify`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Notification sent to ${registration.parent_email}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Notify error:', error);
      toast.error('Failed to send notification');
    } finally {
      setSendingTo(null);
    }
  };

  const handlePromote = async (registration) => {
    setPromotingTo(registration.registration_id);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/waitlist/${registration.registration_id}/promote`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Confirmation email sent!');
        // Refresh waitlist
        loadWaitlist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send offer');
      }
    } catch (error) {
      console.error('Promote error:', error);
      toast.error('Failed to send offer');
    } finally {
      setPromotingTo(null);
    }
  };

  const handleForceConfirm = async (registration) => {
    if (!window.confirm(`Directly add ${registration.player_name || 'this player'} to the session? (Bypasses email confirmation)`)) {
      return;
    }
    
    setPromotingTo(registration.registration_id);
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/sessions/${session.session_id}/waitlist/${registration.registration_id}/force-confirm`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Player confirmed!');
        loadWaitlist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to confirm');
      }
    } catch (error) {
      console.error('Force confirm error:', error);
      toast.error('Failed to confirm');
    } finally {
      setPromotingTo(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-center">
            SESSION WAITLIST
          </DialogTitle>
          {session && (
            <p className="text-sm text-muted-foreground text-center">
              {formatDate(session.date)} • {formatTime(session.start_time)}
            </p>
          )}
        </DialogHeader>

        {sessionInfo && (
          <div className="bg-muted/30 rounded-lg p-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Capacity:</span>
              <span className="font-medium">{sessionInfo.registered_count} / {sessionInfo.capacity}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Spots Available:</span>
              <span className={`font-medium ${sessionInfo.spots_available > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {sessionInfo.spots_available}
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : waitlist.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No one on the waitlist</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {waitlist.length} {waitlist.length === 1 ? 'person' : 'people'} waiting
            </p>
            
            {waitlist.map((item, index) => (
              <div
                key={item.registration_id}
                className="bg-muted/20 rounded-lg p-4 border border-border"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-primary/20 text-primary text-xs font-bold px-2 py-0.5 rounded">
                        #{item.position}
                      </span>
                      <span className="font-medium">{item.player_name}</span>
                      {item.player_age && (
                        <span className="text-xs text-muted-foreground">
                          Age {item.player_age}
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{item.parent_name}</span>
                      </div>
                      {item.parent_email && (
                        <div className="flex items-center gap-2">
                          <EnvelopeSimple className="w-4 h-4" />
                          <span className="truncate">{item.parent_email}</span>
                        </div>
                      )}
                      {item.parent_phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{item.parent_phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-3">
                    {/* Force Add Button - Always available for admin */}
                    <Button
                      size="sm"
                      onClick={() => handleForceConfirm(item)}
                      disabled={promotingTo === item.registration_id}
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid={`force-confirm-${item.registration_id}`}
                    >
                      {promotingTo === item.registration_id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Add to Session
                        </>
                      )}
                    </Button>
                    
                    {/* Status indicator */}
                    {item.status === 'pending_confirmation' && (
                      <div className="text-xs text-yellow-500 text-center">
                        ⏳ Awaiting confirmation
                      </div>
                    )}
                    
                    {/* Offer Spot Button - Sends confirmation email */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePromote(item)}
                      disabled={promotingTo === item.registration_id || item.status === 'pending_confirmation'}
                      data-testid={`promote-waitlist-${item.registration_id}`}
                    >
                      {promotingTo === item.registration_id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <EnvelopeSimple className="w-4 h-4 mr-1" />
                          {item.status === 'pending_confirmation' ? 'Resend Email' : 'Send Invite'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center mb-3">
            <strong>Add to Session</strong> = Directly add them (no email needed)<br/>
            <strong>Send Invite</strong> = Send email asking them to confirm
          </p>
          
          {/* Debug Section */}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => {
              setShowDebug(!showDebug);
              if (!showDebug && !debugData) {
                loadDebugData();
              }
            }}
          >
            <Bug size={14} className="mr-2" />
            {showDebug ? 'Hide' : 'Show'} All Registrations (Debug)
          </Button>
          
          {showDebug && (
            <div className="mt-3 bg-muted/30 rounded-lg p-3 max-h-60 overflow-y-auto">
              {loadingDebug ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : debugData ? (
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between font-medium border-b border-border pb-2">
                    <span>Session Count: {debugData.session_registered_count}</span>
                    <span>Actual: {debugData.actual_total_registrations}</span>
                  </div>
                  <div className="text-muted-foreground mb-2">
                    Status breakdown: {Object.entries(debugData.status_breakdown || {}).map(([status, count]) => (
                      <span key={status} className="mr-2">{status}: {count}</span>
                    ))}
                  </div>
                  {debugData.registrations?.map((reg) => (
                    <div 
                      key={reg.registration_id}
                      className={`p-2 rounded border ${
                        reg.status === 'registered' ? 'border-green-500/30 bg-green-500/10' :
                        reg.status === 'waitlisted' ? 'border-yellow-500/30 bg-yellow-500/10' :
                        reg.status === 'pending_confirmation' ? 'border-blue-500/30 bg-blue-500/10' :
                        'border-red-500/30 bg-red-500/10'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{reg.player_name}</span>
                          <div className="flex gap-2 mt-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                              reg.status === 'registered' ? 'bg-green-500/20 text-green-500' :
                              reg.status === 'waitlisted' ? 'bg-yellow-500/20 text-yellow-500' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {reg.status}
                            </span>
                            <span className="text-muted-foreground">{reg.payment_status}</span>
                          </div>
                        </div>
                        {reg.status !== 'registered' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleForceAdd(reg.player_id, reg.player_name)}
                            disabled={fixingPlayer === reg.player_id}
                            className="text-xs h-7"
                          >
                            {fixingPlayer === reg.player_id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <>
                                <CheckCircle size={12} className="mr-1" />
                                Fix & Add
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground">No debug data available</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
